import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'

import { clsx } from 'clsx'
import { Error, IconSpin, IconXmarkLight } from 'packages/component-library/dist'
import { useTranslation } from 'react-i18next'

import { SelectProductDesktop } from './SelectProduct/desktop'
import appDefaults from '~/portal/portal.defaults.json'
import { SEARCH_TAB_TYPE } from '~/portal/types/search'
import { SelectItem } from '~/portal/types/ui.components'

export type SearchProductsDesktopProps = {
  onSearch: (query: string, type: SEARCH_TAB_TYPE, productId?: string) => void;
  allProductsLabel: string;
  initialText?: string;
  accentColor?: string;
  onChange?: (text: string) => void;
  isLoading?: boolean;
  onInputLengthChange?: (length: number) => void;
  products?: Array<SelectItem>;
  currentProduct?: SelectItem;
  tab: SEARCH_TAB_TYPE;
  handleClear: () => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

export const SearchProductsDesktop = ({
  initialText = '',
  accentColor = '',
  onSearch,
  onChange,
  isLoading = false,
  onInputLengthChange,
  products = [],
  currentProduct,
  allProductsLabel,
  tab,
  handleClear,
  inputRef
}: SearchProductsDesktopProps) => {
  const { t } = useTranslation()
  const [text, setText] = useState<string>(initialText)
  const [prevInputValue, setPrevInputValue] = useState('')
  const [selectOpen, setSelectOpen] = useState(false)

  const selectOptions = [{ id: allProductsLabel, name: allProductsLabel }, ...products]

  const [selectedProduct, setSelectedProduct] = useState<SelectItem>(currentProduct || selectOptions[0])
  const textRef = useRef(text)
  
  useEffect(() => {
    onChange?.(text)
    textRef.current = text 
  }, [text])

  useEffect(() => {
    if (selectedProduct.id === allProductsLabel) {
      onSearch(text, tab)
    } else {
      onSearch(text, tab, selectedProduct.id)
    }
  }, [tab])
  
  const handleInputChange = useCallback((event: FormEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget
    setText(value)
    onInputLengthChange?.(value?.trim().length)

    if (value && value !== prevInputValue) {
      if (selectedProduct.id === allProductsLabel) {
        onSearch(value, tab)
      } else {
        onSearch(value, tab, selectedProduct.id)
      }
      setPrevInputValue(value)
    }
    
  }, [onInputLengthChange, selectedProduct, tab])

  const onClear = useCallback(() => {
    setText('')
    handleClear()
  }, [onInputLengthChange])

  const onChangeProduct = (product: SelectItem) => {
    setSelectedProduct(product)
    if (product.id === allProductsLabel) {
      onSearch(textRef.current, tab)
    } else {
      onSearch(textRef.current, tab, product.id)
    }
  }

  const maxLengthInput = text.length > appDefaults.search.maxQueryLength

  return (
    <div className="w-full">
      <div
        className={clsx('relative w-full rounded-md')}
        data-testid="search"
      >
        <div
          className={clsx(
            'sp-search-input flex items-center rounded border border-(--sp-color-border-strong) w-full text-(--sp-color-text) dark:bg-(--sp-color-surface)',
            !selectOpen && 'hover:border-(--sp-color-accent) focus:border-(--sp-color-accent)',
            maxLengthInput && 'border-error-30!',
          )}
        >
          <div className="shrink-0">
            <SelectProductDesktop
              options={selectOptions}
              accentColor={accentColor}
              defaultValue={currentProduct ? currentProduct : selectOptions[0]}
              onChange={onChangeProduct}
              onOpenChange={setSelectOpen}
            />
          </div>
          <input
            ref={inputRef}
            aria-label="search input"
            type="text"
            value={text}
            onInput={handleInputChange}
            className="grow block w-full py-2 border-0 bg-transparent text-(--sp-color-text) focus:ring-0 text-body2"
          />
          {isLoading ? (
            <div
              className="flex items-center pl-2 pr-3"
              data-testid="search-loading"
            >
              <IconSpin width={16} height={16} />
            </div>
          ) : (
            <div className="flex items-center ml-2 mr-3 cursor-pointer text-(--sp-color-text-muted) hover:text-(--sp-color-text)">
              <div aria-label="search clean" onClick={onClear} className="flex items-center justify-center w-4 h-4 ">
                <IconXmarkLight />
              </div>
            </div>
          )}
        </div>
      </div>
      <div data-testid="search-max-length-error" className={
        clsx('pt-1', maxLengthInput ? 'visible' : 'invisible')
      }>
        <Error>{t('components.ui.searchModal.maxLengthError')}</Error>
      </div>
    </div>
  )
}